import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SubdomainLink } from './SubdomainLink';
import { Menu, X, User, Home, Search, MessageSquare, Crown, LifeBuoy, LogOut, BookOpen, ChevronDown, ChevronRight, CreditCard, History, Star, Building2, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authService, vendorService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import authStorage from '../utils/authStorage';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [displayName, setDisplayName] = useState('demo');
  const [isIndividualListing, setIsIndividualListing] = useState(false);
  const location = useLocation();
  const { isAdmin, roles, logout } = useAuth();
  const isAdminPath = location.pathname.toLowerCase().startsWith('/admin');

  const vendorPaths = ['/my-account/manage-listing', '/my-account/inquiries', '/my-account/upgrade-membership', '/my-account/reviews'];
  const isVendorActive = vendorPaths.some((p) => location.pathname.startsWith(p));
  const isMagazineActive = location.pathname.startsWith('/my-account/magazine');

  const [mobileVendorOpen, setMobileVendorOpen] = useState(() => isVendorActive);
  const [mobileMagazineOpen, setMobileMagazineOpen] = useState(() => isMagazineActive);

  useEffect(() => {
    setMobileVendorOpen(isVendorActive);
    setMobileMagazineOpen(isMagazineActive);
  }, [location.pathname, isVendorActive, isMagazineActive]);

  // Vendor Profile Photo URL State & Helper Function
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const getAdminRoleName = () => {
    if (!roles) return 'Admin Console';
    if (roles.includes('ROLE_SUPER_ADMIN')) return 'Super Admin Console';
    if (roles.includes('ROLE_ADMIN')) return 'Admin Console';
    if (roles.includes('ROLE_SALES_ADMIN')) return 'Sales Console';
    if (roles.includes('ROLE_LISTING_ADMIN')) return 'Listing Console';
    if (roles.includes('ROLE_SUPPORT_ADMIN')) return 'Support Console';
    if (roles.includes('ROLE_MAGAZINE_SALES')) return 'Magazine Sales Console';
    return 'Admin Console';
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateUserProfileData = async () => {
      const storedUser = authStorage.getItem('user');
      if (!storedUser) return;
      
      try {
        const userObj = JSON.parse(storedUser);
        const initialName = userObj.name || (userObj.emailId || userObj.username || 'demo');
        setDisplayName(initialName);
        setProfilePhotoUrl(userObj.photoUrl || '');

        const email = userObj.emailId || userObj.username;
        if (email && email.includes('@')) {
          const res = await authService.getUserProfile(email);
          if (res.success && res.data) {
            const profile = res.data;
            if (profile.name) {
              setDisplayName(profile.name);
            }
            if (profile.photoUrl) {
              setProfilePhotoUrl(profile.photoUrl);
            }
            authStorage.setItem('user', JSON.stringify({
              ...userObj,
              id: profile.id,
              name: profile.name,
              emailId: profile.emailId,
              photoUrl: profile.photoUrl || ''
            }));
          }
        }
      } catch (err) {
        console.error("Error setting Navbar profile data:", err);
      }
    };

    const checkListingType = async () => {
      if (authStorage.getItem('token') && !isAdmin()) {
        try {
          const res = await vendorService.getMyListings();
          if (res.success && res.data) {
            const listings = res.data.listings || (Array.isArray(res.data) ? res.data : []);
            if (listings.length > 0) {
              const first = listings[0];
              const type = (first.listingType || first.listing_type || '').toUpperCase();
              if (type === 'INDIVIDUAL') {
                setIsIndividualListing(true);
              }
            }
          }
        } catch (err) {
          console.error("Error fetching listing type in Navbar:", err);
        }
      }
    };

    updateUserProfileData();
    checkListingType();
    window.addEventListener('storage', updateUserProfileData);
    window.addEventListener('profilePhotoChanged', updateUserProfileData);
    return () => {
      window.removeEventListener('storage', updateUserProfileData);
      window.removeEventListener('profilePhotoChanged', updateUserProfileData);
    };
  }, [isAdmin]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Vendors', path: '/listing' },
  ];

  return (
    <nav className={`${
      isAdminPath 
        ? `fixed ${scrolled ? 'bg-[#23977a] shadow-md' : 'bg-transparent'} border-transparent` 
        : `absolute bg-white border-b border-gray-100 ${scrolled ? 'shadow-md' : ''}`
    } top-0 left-0 right-0 w-full z-[100] transition-all duration-300 flex items-center h-16 sm:h-20`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
        <div className="flex justify-between items-center h-full">
          {/* Main Logo from public folder */}
          <SubdomainLink to={isAdmin() ? "/admin" : "/"} className="flex items-center">
            <img
              src="/Top_Interior_India_1.png"
              alt="Top Interiors India"
              className={`h-9 min-[380px]:h-10 sm:h-11 md:h-12 w-auto object-contain transition-all ${isAdminPath ? 'brightness-0 invert' : ''}`}
            />
          </SubdomainLink>

          {/* Center Links */}
          <div className="hidden lg:flex items-center space-x-12 relative -translate-x-[2%]">
            {!isAdmin() && navLinks.map((link) => (
              <SubdomainLink
                key={link.name}
                to={link.path}
                className={`text-[16px] font-medium transition-colors hover:text-primary ${location.pathname === link.path ? 'text-primary' : 'text-secondary'}`}
              >
                {link.name}
              </SubdomainLink>
            ))}
          </div>

          {/* Right Auth Links */}
          <div className="hidden lg:flex items-center space-x-2">
            {authStorage.getItem('token') ? (
              isAdmin() ? (
                <div className="flex items-center space-x-6">
                  <SubdomainLink to="/admin" className={`text-[14px] font-bold transition-opacity px-2 ${isAdminPath ? 'text-white hover:text-white/80' : 'text-[#2ab391] hover:opacity-85'}`}>
                    {getAdminRoleName()}
                  </SubdomainLink>
                  <button 
                    onClick={logout}
                    className={`text-[14px] font-semibold transition-all active:scale-95 ${
                      isAdminPath 
                        ? 'text-white bg-white/10 hover:bg-white/20 border border-white/20 px-3.5 py-1.5 rounded-lg backdrop-blur-sm' 
                        : 'text-red-600 hover:text-red-700'
                    }`}
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <SubdomainLink to="/my-account/profile" className="flex items-center gap-2 border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50 transition-colors">
                    {profilePhotoUrl ? (
                      <img src={getImageUrl(profilePhotoUrl)} alt="Profile" className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <User size={14} className="text-gray-500" />
                    )}
                    <span className="text-[13px] font-medium text-gray-700">{displayName}</span>
                  </SubdomainLink>
                </div>
              )
            ) : (
              <>
                <SubdomainLink to="/sign-up" className="text-[14px] font-medium text-primary hover:opacity-80 transition-opacity">
                  Sign Up
                </SubdomainLink>
                <span className="text-gray-300">|</span>
                <SubdomainLink to="/login" className="text-[14px] font-medium text-primary hover:opacity-80 transition-opacity">
                  Login
                </SubdomainLink>
              </>
            )}
          </div>

          {/* Mobile Menu Button & Guest Auth Links */}
          <div className="lg:hidden flex items-center gap-1 sm:gap-2">
            {!authStorage.getItem('token') && (
              <div className="flex items-center gap-1.5 mr-1">
                <SubdomainLink
                  to="/login"
                  className="text-xs font-semibold text-primary border border-primary/20 hover:bg-primary/5 transition-all py-1.5 px-3 rounded-lg bg-white"
                >
                  Login
                </SubdomainLink>
                <SubdomainLink
                  to="/sign-up"
                  className="text-xs font-semibold text-white bg-primary hover:bg-primary/90 transition-all py-1.5 px-3 rounded-lg shadow-xs"
                >
                  Sign Up
                </SubdomainLink>
              </div>
            )}
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className={`p-2 rounded-lg transition-colors ${
                isAdminPath 
                  ? 'text-white hover:bg-white/10' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Toggle menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-xs lg:hidden"
            />

            {/* Sliding Drawer */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="fixed top-0 left-0 bottom-0 z-[120] w-[300px] max-w-[85vw] bg-white shadow-xl flex flex-col lg:hidden border-r border-gray-200/80 text-left"
            >
              {authStorage.getItem('token') && !isAdmin() ? (
                /* Profile Header Card for Logged-In Vendors */
                <div className="p-4 border-b border-gray-100 bg-slate-50/60 relative">
                  <button 
                    onClick={() => setIsOpen(false)} 
                    className="absolute top-3.5 right-3.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 p-1.5 rounded-full transition-colors"
                    aria-label="Close menu"
                  >
                    <X size={18} />
                  </button>
                  
                  <div className="flex items-center gap-3 pr-8">
                    {/* Avatar */}
                    <SubdomainLink 
                      to="/my-account/profile"
                      onClick={() => setIsOpen(false)}
                      className="w-11 h-11 bg-teal-50 text-[#2ab391] rounded-full border border-teal-100 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-2xs hover:opacity-90 transition-opacity"
                    >
                      {profilePhotoUrl ? (
                        <img src={getImageUrl(profilePhotoUrl)} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User size={22} className="text-[#2ab391]" />
                      )}
                    </SubdomainLink>

                    {/* Name & My Profile Action */}
                    <div className="flex flex-col min-w-0 flex-1 justify-center">
                      <SubdomainLink
                        to="/my-account/profile"
                        onClick={() => setIsOpen(false)}
                        className="font-semibold text-sm text-gray-900 truncate leading-snug hover:text-[#2ab391] transition-colors"
                      >
                        {displayName}
                      </SubdomainLink>
                      <SubdomainLink 
                        to="/my-account/profile" 
                        onClick={() => setIsOpen(false)} 
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2ab391] hover:text-[#1e876e] transition-colors mt-0.5 w-fit"
                      >
                        <User size={12} />
                        <span>My Profile</span>
                      </SubdomainLink>
                    </div>
                  </div>
                </div>
              ) : (
                /* Standard Minimal Header for Admin/Guests */
                <div className="flex items-center justify-end px-5 py-4 border-b border-gray-100 bg-slate-50/60">
                  <button 
                    onClick={() => setIsOpen(false)} 
                    className="text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 p-1.5 rounded-full transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              )}

              {/* Menu Navigation Content */}
              <div className="flex-1 overflow-y-auto p-3 space-y-0.5">
                {/* General Explore Section Header */}
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Explore
                </div>

                {/* Explore Links */}
                {[
                  { name: 'Home', path: '/', icon: <Home size={17} /> },
                  { name: 'Search Vendor', path: '/listing', icon: <Search size={17} /> }
                ].map(link => (
                  <SubdomainLink
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.path 
                        ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <span className={location.pathname === link.path ? 'text-[#2ab391]' : 'text-gray-400'}>
                      {link.icon}
                    </span>
                    <span>{link.name}</span>
                  </SubdomainLink>
                ))}

                {/* Account Section Header */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div className="px-3 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    Account
                  </div>
                )}

                {/* ── Vendor Collapsible Menu ─────────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div>
                    <button
                      onClick={() => setMobileVendorOpen(!mobileVendorOpen)}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        mobileVendorOpen ? 'text-gray-900 bg-gray-50' : 'text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Building2 size={17} className="text-gray-500" />
                        <span>Vendor</span>
                      </span>
                      {mobileVendorOpen ? <ChevronDown size={15} className="text-gray-400" /> : <ChevronRight size={15} className="text-gray-400" />}
                    </button>

                    {mobileVendorOpen && (
                      <div className="space-y-0.5 mt-0.5">
                        {[
                          { name: 'Vendor Listing', path: '/my-account/manage-listing', icon: <FileText size={16} /> },
                          { name: 'Inquiry', path: '/my-account/inquiries', icon: <MessageSquare size={16} /> },
                          { name: 'Upgrade Membership', path: '/my-account/upgrade-membership', icon: <Crown size={16} /> },
                          { name: 'Reviews', path: '/my-account/reviews', icon: <Star size={16} /> },
                        ]
                        .filter(link => !(isIndividualListing && link.path === '/my-account/upgrade-membership'))
                        .map(link => (
                          <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-3 pl-7 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              location.pathname === link.path 
                                ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                          >
                            <span className={location.pathname === link.path ? 'text-[#2ab391]' : 'text-gray-400'}>
                              {link.icon}
                            </span>
                            <span>{link.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Magazine Collapsible Menu ────────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div>
                    <button
                      onClick={() => setMobileMagazineOpen(!mobileMagazineOpen)}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        mobileMagazineOpen ? 'text-gray-900 bg-gray-50' : 'text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <BookOpen size={17} className="text-gray-500" />
                        <span>Magazine</span>
                      </span>
                      {mobileMagazineOpen ? <ChevronDown size={15} className="text-gray-400" /> : <ChevronRight size={15} className="text-gray-400" />}
                    </button>

                    {mobileMagazineOpen && (
                      <div className="space-y-0.5 mt-0.5">
                        {[
                          { name: 'Your Subscription', path: '/my-account/magazine/subscription', icon: <CreditCard size={16} /> },
                          { name: 'Subscribe', path: '/my-account/magazine/subscribe', icon: <BookOpen size={16} /> },
                          { name: 'History of Payment', path: '/my-account/magazine/history', icon: <History size={16} /> },
                        ].map(link => (
                          <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-3 pl-7 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              location.pathname === link.path 
                                ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                          >
                            <span className={location.pathname === link.path ? 'text-[#2ab391]' : 'text-gray-400'}>
                              {link.icon}
                            </span>
                            <span>{link.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Help & Support Top-level Menu ─────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <Link
                    to="/my-account/support-tickets"
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === '/my-account/support-tickets' 
                        ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <LifeBuoy size={17} className={location.pathname === '/my-account/support-tickets' ? 'text-[#2ab391]' : 'text-gray-400'} />
                    <span>Help & Support</span>
                  </Link>
                )}

                {/* Auth Actions or Admin Control */}
                {authStorage.getItem('token') && (
                  <div className="border-t border-gray-100 mt-3 pt-3">
                    {isAdmin() ? (
                      <div className="space-y-0.5">
                        <div className="px-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Administration
                        </div>
                        <SubdomainLink
                          to="/admin"
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[#2ab391] hover:bg-gray-100"
                        >
                          <User size={17} />
                          <span>{getAdminRoleName()}</span>
                        </SubdomainLink>
                        <button
                          onClick={() => {
                            setIsOpen(false);
                            logout();
                          }}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 w-full text-left transition-colors"
                        >
                          <LogOut size={17} />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    ) : (
                      /* Vendor Log Out */
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          logout();
                        }}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 w-full text-left transition-colors"
                      >
                        <LogOut size={17} />
                        <span>Sign Out</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
